import { useEffect, useState } from 'react';
import {
  onAuthStateChanged,
  signOut as firebaseSignOut,
  signInWithEmailAndPassword as firebaseSignInWithEmail,
  createUserWithEmailAndPassword as firebaseCreateUser,
  sendPasswordResetEmail as firebaseSendPasswordReset,
  signInAnonymously as firebaseSignInAnonymously,
  type User,
} from 'firebase/auth';
import { initFirebase } from './firebase';

/**
 * Hook to access Firebase authentication state
 * Returns the current user and loading state
 */
export function useFirebaseAuth() {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        try {
            const { auth } = initFirebase();

            const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
                setUser(currentUser);
                setLoading(false);
            }, (authError) => {
                console.error('Auth state change error:', authError);
                setError(authError as Error);
                setLoading(false);
            });

            return () => unsubscribe();
        } catch (initError) {
            console.error('Firebase initialization error:', initError);
            setError(initError as Error);
            setLoading(false);
        }
    }, []);

    return { user, loading, isAuthenticated: !!user, error };
}

/**
 * Sign out the current user
 */
export async function signOut() {
    const { auth } = initFirebase();
    await firebaseSignOut(auth);
}

/**
 * Sign in with email and password
 */
export async function signInWithEmail(email: string, password: string) {
    const { auth } = initFirebase();
    return firebaseSignInWithEmail(auth, email, password);
}

/**
 * Create a new account with email and password
 */
export async function createAccount(email: string, password: string) {
    const { auth } = initFirebase();
    return firebaseCreateUser(auth, email, password);
}

/**
 * Send a password reset email
 */
export async function sendPasswordReset(email: string) {
    const { auth } = initFirebase();
    return firebaseSendPasswordReset(auth, email);
}

/**
 * Sign in anonymously (for customer app)
 */
export async function signInAnonymously() {
    const { auth } = initFirebase();
    return firebaseSignInAnonymously(auth);
}

/**
 * Subscribe to auth state changes (non-hook version)
 * Returns unsubscribe function
 */
export function onAuthStateChange(callback: (user: User | null) => void): () => void {
    const { auth } = initFirebase();
    return onAuthStateChanged(auth, callback);
}

/**
 * Get the current user synchronously (may be null if auth not initialized)
 */
export function getCurrentUser(): User | null {
    const { auth } = initFirebase();
    return auth.currentUser;
}
